import React from "react";
import Seo from "../components/Seo.jsx";

/* Profile view screen — hydrates from app-level cached profile data when
   available, and only refetches GET /profile/<id> when it needs a fresh copy
   after a mutation. UI-only extras (color, initials, lastScan) still come from
   the prop; everything else is read straight from business_profile.

   No cached/local copies of the profile fields. The opportunity map is fully
   controlled from here: when editing starts, we snapshot the server's plan into
   `editPlan`; on cancel we drop it, on save we persist via the search_plan
   endpoint (which refreshes the profile embedding) and refetch. */

const EMPTY_SEARCH_PLAN = {
  target_customers: [],
  jobs_to_be_done: [],
  pain_points: [],
  trigger_phrases: [],
  suggested_subreddits: [],
  negative_matches: [],
  search_themes: [],
};

// Accepts a bare name ("AskReddit"), a path ("/r/AskReddit"), or a full Reddit
// URL ("https://www.reddit.com/r/AskReddit/"). Returns just the subreddit
// name with original casing — backend dedups and ingests case-insensitively.
function parseSubredditName(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  const m = s.match(/(?:^|[\/.])r\/([A-Za-z0-9_]{2,21})/i);
  if (m) return m[1];
  return s.replace(/^\/+/, "").split(/[\/?#\s]/)[0];
}

function formatSubscribers(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return "";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(num >= 10_000_000 ? 0 : 1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(num >= 10_000 ? 0 : 1) + "k";
  return String(num);
}

function inferSignal(subscribers) {
  const n = Number(subscribers);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n >= 500_000) return "high";
  if (n >= 50_000) return "medium";
  return "low";
}

function SubredditAvatar({ name, icon }) {
  const label = String(name || "").trim().replace(/^r\//i, "");
  if (icon) {
    return (
      <span className="ob2-sub-avatar" aria-hidden="true">
        <img className="ob2-sub-avatar-img" src={icon} alt="" loading="lazy" />
      </span>
    );
  }
  return (
    <span className="ob2-sub-avatar ob2-sub-avatar-fallback" aria-hidden="true">
      {label ? label.slice(0, 1).toUpperCase() : "r"}
    </span>
  );
}

function ProfileScreen({ profile, onUpdateProfile, demoProfile = null, hydratedProfile = null, onRefreshProfile = null }) {
  const [editing, setEditing] = React.useState(false);
  const [serverProfile, setServerProfile] = React.useState(() => hydratedProfile || demoProfile || null);
  const [loading, setLoading] = React.useState(() => !hydratedProfile && !demoProfile);
  const [error, setError] = React.useState(null);

  // Opportunity map state. editPlan is non-null only while editing.
  const [editPlan, setEditPlan] = React.useState(null);
  const [savingMap, setSavingMap] = React.useState(false);
  const [rerunning, setRerunning] = React.useState(false);
  const [mapError, setMapError] = React.useState(null);

  // Subreddit radar add-row state — purely UI.
  const [editRadarDraft, setEditRadarDraft] = React.useState(null);
  const [addingSub, setAddingSub] = React.useState(false);
  const [newSubName, setNewSubName] = React.useState("");
  const [savingRadar, setSavingRadar] = React.useState(false);
  const [autosavingRadar, setAutosavingRadar] = React.useState(false);
  const [subredditIcons, setSubredditIcons] = React.useState({});
  const radarAutosaveRef = React.useRef({ inFlight: false, queuedDraft: null });
  // Mirror of editRadarDraft for synchronous reads inside async flows. The
  // setState updater isn't guaranteed to run synchronously in React 18, so
  // we can't rely on closure assignment from inside it.
  const editRadarDraftRef = React.useRef(null);
  React.useEffect(() => {
    editRadarDraftRef.current = editRadarDraft;
  }, [editRadarDraft]);

  const profileId = window.api?.storage?.readProfileId?.() || null;
  const isDemoProfile = !!demoProfile;

  const fetchProfile = React.useCallback(async (options = {}) => {
    const { force = false } = options;
    if (demoProfile) {
      setServerProfile(demoProfile);
      setLoading(false);
      setError(null);
      return demoProfile;
    }
    if (!profileId) {
      setServerProfile(null);
      setLoading(false);
      return null;
    }
    if (!force && hydratedProfile) {
      setServerProfile(hydratedProfile);
      setLoading(false);
      setError(null);
      return hydratedProfile;
    }
    setLoading(true);
    setError(null);
    try {
      const next = onRefreshProfile
        ? await onRefreshProfile(profileId, { force })
        : (await window.api.profile.get(profileId))?.profile || null;
      setServerProfile(next || null);
      return next || null;
    } catch (e) {
      setError(e.message || "Could not load profile");
      return null;
    } finally {
      setLoading(false);
    }
  }, [demoProfile, hydratedProfile, onRefreshProfile, profileId]);

  React.useEffect(() => {
    if (hydratedProfile) {
      setServerProfile(hydratedProfile);
      setLoading(false);
      setError(null);
      return;
    }
    fetchProfile();
  }, [fetchProfile, hydratedProfile]);

  const serverPlan = serverProfile?.search_plan || null;
  const editingMap = editPlan !== null;
  const editingRadar = editRadarDraft !== null;
  const planInView = editingMap ? editPlan : serverPlan;

  const startEditMap = () => {
    setMapError(null);
    setEditPlan(serverPlan ? { ...EMPTY_SEARCH_PLAN, ...serverPlan } : { ...EMPTY_SEARCH_PLAN });
  };

  const cancelEditMap = () => {
    setEditPlan(null);
    setMapError(null);
  };

  const rerunAnalysis = async () => {
    if (!profileId || rerunning) return;
    setRerunning(true);
    setMapError(null);
    try {
      // Re-runs server-side expansion and persists the new plan + embedding.
      await window.api.profile.expandSaved(profileId);
      await fetchProfile({ force: true });
      setEditPlan(null);
    } catch (e) {
      setMapError(e.message || "Could not re-run analysis");
    } finally {
      setRerunning(false);
    }
  };

  const saveMap = async (plan) => {
    if (!profileId || savingMap || !plan) return;
    setSavingMap(true);
    setMapError(null);
    try {
      // saveSearchPlan persists the plan AND re-embeds the business profile
      // on the backend (set_business_profile_search_plan -> _upsert_profile_embedding).
      await window.api.profile.saveSearchPlan(profileId, plan);
      await fetchProfile({ force: true });
      setEditPlan(null);
    } catch (e) {
      setMapError(e.message || "Could not save search plan");
    } finally {
      setSavingMap(false);
    }
  };

  const buildProfileOverridePayload = React.useCallback((subreddits) => {
    const toList = (value) =>
      String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
    return {
      name: serverProfile?.product_name || "",
      website: serverProfile?.website || "",
      description: serverProfile?.short_description || "",
      customers: serverProfile?.target_customers || "",
      useCases: toList(serverProfile?.use_cases),
      painPoints: toList(serverProfile?.pain_points),
      competitors: toList(serverProfile?.competitors),
      keywords: toList(serverProfile?.positive_keywords),
      avoid: toList(serverProfile?.negative_keywords),
      subreddits,
    };
  }, [serverProfile]);

  const persistRadar = async ({ nextSubreddits, nextSuggestedSubreddits, errorMessage }) => {
    if (!profileId || savingRadar) return false;
    setSavingRadar(true);
    setMapError(null);
    try {
      await window.api.profile.saveRadar(profileId, {
        subreddits: nextSubreddits,
        suggestedSubreddits: nextSuggestedSubreddits,
      });
      onUpdateProfile?.(buildProfileOverridePayload(nextSubreddits), {
        silent: true,
      });
      await fetchProfile({ force: true });
      return true;
    } catch (e) {
      setMapError(e.message || errorMessage);
      return false;
    } finally {
      setSavingRadar(false);
    }
  };

  const startEditRadar = () => {
    setMapError(null);
    setAddingSub(true);
    setNewSubName("");
    setEditRadarDraft({
      // Start from the merged radar view so anything visible here is what
      // gets written back into business_profile.subreddits on save.
      subreddits: radarSubs.map((s) => s.name.replace(/^r\//i, "")),
      suggested_subreddits: [ ...((serverPlan?.suggested_subreddits) || []) ],
    });
  };

  const cancelEditRadar = () => {
    setEditRadarDraft(null);
    setAddingSub(false);
    setNewSubName("");
    setMapError(null);
    radarAutosaveRef.current.queuedDraft = null;
  };

  const buildRadarPayload = React.useCallback((draft) => {
    const nextSuggestedSubreddits = draft?.suggested_subreddits || [];
    const nextSubreddits = Array.from(new Set([
      ...(draft?.subreddits || []).map((s) => String(s || "").trim().replace(/^r\//i, "")),
      ...nextSuggestedSubreddits.map((s) => String(s?.name || "").trim().replace(/^r\//i, "")),
    ].filter(Boolean)));
    return {
      nextSubreddits,
      nextSuggestedSubreddits,
    };
  }, []);

  const queueRadarAutosave = React.useCallback((draft) => {
    if (!profileId || !draft) return;
    radarAutosaveRef.current.queuedDraft = draft;
    if (radarAutosaveRef.current.inFlight) return;

    radarAutosaveRef.current.inFlight = true;
    setAutosavingRadar(true);

    void (async () => {
      try {
        while (radarAutosaveRef.current.queuedDraft) {
          const nextDraft = radarAutosaveRef.current.queuedDraft;
          radarAutosaveRef.current.queuedDraft = null;
          const { nextSubreddits, nextSuggestedSubreddits } = buildRadarPayload(nextDraft);
          try {
            await window.api.profile.saveRadar(profileId, {
              subreddits: nextSubreddits,
              suggestedSubreddits: nextSuggestedSubreddits,
            });
            onUpdateProfile?.(buildProfileOverridePayload(nextSubreddits), {
              silent: true,
            });
          } catch (e) {
            setMapError(e.message || "Could not auto-save subreddit radar");
          }
        }
        // No fetchProfile here — editRadarDraft is the source of truth while
        // editing, and a refetch flips `loading` which flashes the entire
        // screen to "Loading…". Done-editing (persistRadar) refreshes once.
      } finally {
        radarAutosaveRef.current.inFlight = false;
        setAutosavingRadar(false);
      }
    })();
  }, [
    buildProfileOverridePayload,
    buildRadarPayload,
    onUpdateProfile,
    profileId,
  ]);

  const saveRadar = async () => {
    if (!editRadarDraft) return;
    const { nextSubreddits, nextSuggestedSubreddits } = buildRadarPayload(editRadarDraft);
    const ok = await persistRadar({
      nextSubreddits,
      nextSuggestedSubreddits,
      errorMessage: "Could not save subreddit radar",
    });
    if (ok) {
      setEditRadarDraft(null);
      setAddingSub(false);
      setNewSubName("");
    }
  };

  const addSubreddit = async (raw) => {
    if (!editRadarDraft || savingRadar) return;
    const clean = parseSubredditName(raw);
    if (!clean) return;

    const existing = editRadarDraft.suggested_subreddits || [];
    const dupe = existing.some(
      (s) => String(s?.name || "").replace(/^r\//i, "").toLowerCase() === clean.toLowerCase()
    );
    const inBaseList = (editRadarDraft.subreddits || []).some(
      (s) => String(s || "").replace(/^r\//i, "").trim().toLowerCase() === clean.toLowerCase()
    );
    if (dupe || inBaseList) {
      setNewSubName("");
      return;
    }

    const nextDraft = {
      ...editRadarDraft,
      subreddits: [
        ...(editRadarDraft.subreddits || []),
        clean,
      ],
      suggested_subreddits: [
        ...(editRadarDraft.suggested_subreddits || []),
        { name: clean, size: "", reason: "", signal: "", community_icon: "" },
      ],
    };
    const { nextSubreddits } = buildRadarPayload(nextDraft);
    const optimisticDraft = {
      ...nextDraft,
      subreddits: [...nextSubreddits],
    };
    setMapError(null);
    setEditRadarDraft(optimisticDraft);
    editRadarDraftRef.current = optimisticDraft;
    queueRadarAutosave(optimisticDraft);
    setNewSubName("");

    // Hydrate the row from /subreddit/info (DB-first, PRAW fallback).
    // On success, patch the suggested_subreddits entry with size/signal/note
    // and re-queue an autosave so the metadata persists without the user
    // having to click Done editing.
    void (async () => {
      try {
        const resp = await window.api.subreddit.info(clean);
        const info = resp?.subreddit;
        if (!info) return;
        const size = formatSubscribers(info.subscribers);
        const signal = inferSignal(info.subscribers);
        const reason = info.public_description || info.title || "";
        const key = clean.toLowerCase();
        const current = editRadarDraftRef.current;
        if (!current) return;
        const nextSuggested = (current.suggested_subreddits || []).map((entry) => {
          if (!entry?.name) return entry;
          if (entry.name.replace(/^r\//i, "").toLowerCase() !== key) return entry;
          return {
            ...entry,
            community_icon: info.community_icon || entry.community_icon || "",
            size: size || entry.size,
            signal: signal || entry.signal,
            reason: reason || entry.reason,
          };
        });
        const patchedDraft = { ...current, suggested_subreddits: nextSuggested };
        if (info.community_icon) {
          setSubredditIcons((prev) => ({ ...prev, [key]: info.community_icon }));
        }
        editRadarDraftRef.current = patchedDraft;
        setEditRadarDraft(patchedDraft);
        queueRadarAutosave(patchedDraft);
      } catch (e) {
        // Non-fatal — row keeps its bare presentation; the optimistic save
        // already persisted the entry.
      }
    })();
  };

  const removeSubreddit = (name) => {
    if (!editRadarDraft || savingRadar) return;
    const clean = String(name || "").trim().replace(/^r\//i, "").toLowerCase();
    if (!clean) return;
    setEditRadarDraft((prev) => ({
      ...prev,
      subreddits: (prev?.subreddits || []).filter(
        (item) => String(item || "").trim().replace(/^r\//i, "").toLowerCase() !== clean
      ),
      suggested_subreddits: (prev?.suggested_subreddits || []).filter(
        (item) => String(item?.name || "").trim().replace(/^r\//i, "").toLowerCase() !== clean
      ),
    }));
  };

  // Per-row quick remove available outside the edit flow. Drops the subreddit
  // from the merged radar view and persists straight away. We update
  // serverProfile optimistically so the row disappears immediately rather than
  // flashing the whole screen to "Loading…" via a forced refetch.
  const quickRemoveSubreddit = async (name) => {
    if (!profileId || isDemoProfile || editingRadar) return;
    const clean = String(name || "").trim().replace(/^r\//i, "").toLowerCase();
    if (!clean) return;
    const nextSubreddits = radarSubs
      .map((s) => s.name.replace(/^r\//i, ""))
      .filter((n) => n.toLowerCase() !== clean);
    const nextSuggestedSubreddits = (serverPlan?.suggested_subreddits || []).filter(
      (s) => String(s?.name || "").trim().replace(/^r\//i, "").toLowerCase() !== clean
    );
    setMapError(null);
    setServerProfile((prev) => (prev ? {
      ...prev,
      subreddits: nextSubreddits.join(","),
      search_plan: prev.search_plan
        ? { ...prev.search_plan, suggested_subreddits: nextSuggestedSubreddits }
        : prev.search_plan,
    } : prev));
    try {
      await window.api.profile.saveRadar(profileId, {
        subreddits: nextSubreddits,
        suggestedSubreddits: nextSuggestedSubreddits,
      });
      onUpdateProfile?.(buildProfileOverridePayload(nextSubreddits), { silent: true });
    } catch (e) {
      setMapError(e.message || "Could not remove subreddit");
      // Roll back to the authoritative copy on failure.
      fetchProfile({ force: true });
    }
  };

  const splitList = (s) =>
    (s || "").split(",").map((x) => x.trim()).filter(Boolean);

  // UI-only fields come from the prop. Everything substantive comes from the
  // server profile — no silent fallbacks that could mask stale state.
  const uiOnly = {
    id: profile?.id,
    color: profile?.color,
    initials: profile?.initials,
    lastScan: profile?.lastScan,
  };
  const display = {
    ...uiOnly,
    name: serverProfile?.product_name || "",
    website: serverProfile?.website || "",
    faviconUrl: serverProfile?.favicon_url || profile?.faviconUrl || "",
    description: serverProfile?.short_description || "",
    customers: serverProfile?.target_customers || "",
    useCases: splitList(serverProfile?.use_cases),
    painPoints: splitList(serverProfile?.pain_points),
    competitors: splitList(serverProfile?.competitors),
    keywords: splitList(serverProfile?.positive_keywords),
    avoid: splitList(serverProfile?.negative_keywords),
    subreddits: splitList(serverProfile?.subreddits),
  };

  // Subreddit radar list: merge the CSV `subreddits` column with the richer
  // `search_plan.suggested_subreddits` entries (which carry signal/size/reason).
  // Plan entries win on metadata.
  const radarSubs = React.useMemo(() => {
    const byName = new Map();
    const baseSubs = editingRadar ? ((editRadarDraft?.subreddits) || []) : (display.subreddits || []);
    for (const raw of baseSubs) {
      const clean = String(raw || "").trim().replace(/^r\//i, "");
      if (!clean) continue;
      byName.set(clean.toLowerCase(), {
        name: "r/" + clean,
        members: "",
        note: "",
        signal: "",
        communityIcon: "",
      });
    }
    const planSubs = editingRadar
      ? ((editRadarDraft?.suggested_subreddits) || [])
      : ((serverPlan?.suggested_subreddits) || []);
    for (const s of planSubs) {
      const clean = String(s?.name || "").trim().replace(/^r\//i, "");
      if (!clean) continue;
      byName.set(clean.toLowerCase(), {
        name: "r/" + clean,
        members: s.size || "",
        note: s.reason || "",
        signal: s.signal || "",
        communityIcon: s.community_icon || "",
      });
    }
    return Array.from(byName.values());
  }, [display.subreddits, editRadarDraft, editingRadar, serverPlan]);

  React.useEffect(() => {
    const missing = radarSubs.filter((s) => {
      const key = s.name.replace(/^r\//i, "").toLowerCase();
      return key && !s.communityIcon && !subredditIcons[key];
    });
    if (!missing.length) return;
    let cancelled = false;
    void (async () => {
      const found = {};
      await Promise.all(missing.map(async (s) => {
        const clean = s.name.replace(/^r\//i, "");
        try {
          const resp = await window.api?.subreddit?.info?.(clean);
          const icon = resp?.subreddit?.community_icon || "";
          if (icon) found[clean.toLowerCase()] = icon;
        } catch (e) {}
      }));
      if (cancelled || !Object.keys(found).length) return;
      setSubredditIcons((prev) => ({ ...prev, ...found }));
    })();
    return () => {
      cancelled = true;
    };
  }, [radarSubs, subredditIcons]);

  const editProfileInitial = React.useMemo(() => ({
    name: display.name,
    website: display.website,
    description: display.description,
    customers: display.customers,
    useCases: (serverPlan?.jobs_to_be_done?.length ? serverPlan.jobs_to_be_done : display.useCases),
    painPoints: (serverPlan?.pain_points?.length ? serverPlan.pain_points : display.painPoints),
    competitors: display.competitors,
    keywords: display.keywords,
    avoid: display.avoid,
    subreddits: radarSubs.map((s) => s.name.replace(/^r\//i, "")),
  }), [
    display.avoid,
    display.competitors,
    display.customers,
    display.description,
    display.keywords,
    display.name,
    display.painPoints,
    display.useCases,
    display.website,
    radarSubs,
    serverPlan,
  ]);

  if (loading) {
    return (
      <div className="page screen-enter" style={{ maxWidth: 920 }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Business profile</h1>
            <p className="page-subtitle">Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page screen-enter" style={{ maxWidth: 920 }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Business profile</h1>
            <p className="page-subtitle" style={{ color: "var(--danger)" }}>{error}</p>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={fetchProfile}>
            <window.I.Refresh size={13} />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="page screen-enter" style={{ maxWidth: 760 }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Edit your profile</h1>
            <p className="page-subtitle">Changes apply to the next scan.</p>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditing(false)}>
            <window.I.X size={14} />
            Cancel
          </button>
        </div>
        <window.ProfileSetupScreen
          mode="edit"
          initial={editProfileInitial}
          onSubmit={(form) => {
            onUpdateProfile(form);
            setEditing(false);
            fetchProfile();
          }}
          onBack={() => setEditing(false)}
        />
      </div>
    );
  }

  return (
    <div className="page screen-enter" style={{ maxWidth: 920 }}>
      <Seo title="Business profile" noindex />
        <div className="page-header">
          <div>
            <h1 className="page-title">Your profile</h1>
            <p className="page-subtitle">
              {isDemoProfile
                ? "A sample workspace showing how Community Ninja learns what matters."
                : "What we use to decide what's relevant."}
            </p>
          </div>
          {!isDemoProfile && (
            <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)}>
              <window.I.Edit size={14} />
              Edit profile
            </button>
          )}
        </div>

      <div className="card profile-overview-card" style={{ padding: 22, marginBottom: 14 }}>
        <div className="profile-overview-head" style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
          <div className="profile-mark" style={!display.faviconUrl ? { background: display.color, width: 44, height: 44, fontSize: 16, borderRadius: 12 } : { width: 44, height: 44, borderRadius: 12 }}>
            {display.faviconUrl ? (
              <img className="profile-mark-img" src={display.faviconUrl} alt="" />
            ) : (
              display.initials
            )}
          </div>
          <div className="profile-overview-ident">
            <div className="profile-overview-name" style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.015em" }}>{display.name}</div>
            <div className="profile-overview-site" style={{ fontSize: 13, color: "var(--text-tertiary)" }}>{display.website}</div>
          </div>
        </div>

        <div className="profile-overview-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 22 }}>
          <ProfileBlock label="Description">{display.description}</ProfileBlock>
          <ProfileBlock label="Target customers">{display.customers}</ProfileBlock>
          <ProfileBlock label="Use cases">
            <div className="chip-list">
              {display.useCases.map((u) => <span key={u} className="pill pill-neutral">{u}</span>)}
            </div>
          </ProfileBlock>
          <ProfileBlock label="Pain points">
            <div className="chip-list">
              {display.painPoints.map((p) => <span key={p} className="pill pill-neutral">{p}</span>)}
            </div>
          </ProfileBlock>
          <ProfileBlock label="Keywords to include">
            <div className="chip-list">
              {display.keywords.map((k) => <span key={k} className="pill pill-accent">{k}</span>)}
            </div>
          </ProfileBlock>
          <ProfileBlock label="Keywords to avoid">
            <div className="chip-list">
              {display.avoid.map((k) => <span key={k} className="pill pill-neutral">{k}</span>)}
            </div>
          </ProfileBlock>
          <ProfileBlock label="Competitors">
            <div className="chip-list">
              {display.competitors.map((c) => <span key={c} className="pill pill-neutral">{c}</span>)}
            </div>
          </ProfileBlock>
        </div>
      </div>

      <section className="ob2-radar-section" style={{ marginBottom: 14 }}>
        <div className="ob2-section-head ob2-radar-section-head">
          <div>
            <div className="ob2-section-title">
              <window.I.Target size={14} />
              Subreddit radar
            </div>
            <div className="ob2-section-desc">
              <strong>{radarSubs.length}</strong>{" "}
              {radarSubs.length === 1 ? "community" : "communities"} ·
              {" "}<em>scanned on every run</em>
              {autosavingRadar ? <> · <span>saving changes…</span></> : null}
            </div>
          </div>
          {editingRadar ? (
            <div className="profile-radar-actions" style={{ display: "flex", gap: 8 }}>
              <button
                className="btn btn-ghost btn-sm"
                onClick={cancelEditRadar}
                disabled={savingRadar}
              >
                Cancel
              </button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={saveRadar}
                disabled={!profileId || savingRadar || isDemoProfile}
                title={!profileId ? "Sign in to edit subreddit radar" : ""}
              >
                {savingRadar ? "Saving…" : "Done editing"}
              </button>
            </div>
          ) : (
            <button
              className="ob2-add-btn"
              onClick={startEditRadar}
              disabled={!profileId || savingRadar || isDemoProfile}
              title={!profileId ? "Sign in to edit subreddit radar" : ""}
            >
              <window.I.Edit size={12} />
              Edit subreddit radar
            </button>
          )}
        </div>

        <div className="ob2-sublist">
          {radarSubs.map((s) => {
            const hasSignal = s.signal === "high" || s.signal === "medium" || s.signal === "low";
            const hasMembers = s.members && s.members !== "—" && s.members !== "unknown";
            const icon = s.communityIcon || subredditIcons[s.name.replace(/^r\//i, "").toLowerCase()] || "";
            return (
            <div key={s.name} className="ob2-subrow">
              {hasSignal ? (
                <div className="ob2-sub-signal" title={`Signal: ${s.signal}`}>
                  <window.SignalBars level={s.signal} />
                </div>
              ) : (
                <div className="ob2-sub-signal" aria-hidden="true" />
              )}
              <SubredditAvatar name={s.name} icon={icon} />
              <div className="ob2-sub-main">
                <div className="ob2-sub-name-row">
                  <span className="ob2-sub-name">{s.name}</span>
                  {hasMembers && (
                    <span className="ob2-sub-members">{s.members} members</span>
                  )}
                  {hasSignal && (
                    <span className={"ob2-sub-tier ob2-sub-tier-" + s.signal}>
                      {s.signal === "high" ? "Strong signal" :
                       s.signal === "medium" ? "Moderate" : "Adjacent"}
                    </span>
                  )}
                </div>
                {s.note && <div className="ob2-sub-note">{s.note}</div>}
              </div>
              {(editingRadar || (!isDemoProfile && profileId)) && (
                <button
                  className="ob2-sub-remove"
                  onClick={() => editingRadar ? removeSubreddit(s.name) : quickRemoveSubreddit(s.name)}
                  disabled={savingRadar}
                  title={`Remove ${s.name} from subreddit radar`}
                  aria-label={`Remove ${s.name} from subreddit radar`}
                >
                  <window.I.X size={14} />
                </button>
              )}
            </div>
          );
          })}

          {addingSub && (
            <AddSubredditRow
              value={newSubName}
              onChange={setNewSubName}
              onSubmit={() => addSubreddit(newSubName)}
              onCancel={() => { setNewSubName(""); }}
              busy={savingRadar}
            />
          )}

          {radarSubs.length === 0 && !addingSub && (
            <div className="ob2-sublist-empty">
              <window.I.Layers size={14} />
              No subreddits on the radar yet. {editingRadar ? "Add one to start scanning." : "Edit the radar to add one."}
            </div>
          )}
        </div>
      </section>

      <div className="profile-map-head" style={{ display: "flex", alignItems: "center", gap: 8, padding: "16px 4px 8px" }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, letterSpacing: "-0.015em", margin: 0 }}>Opportunity map</h2>
        <span className="pill pill-accent">Used in scans</span>
        <div className="spacer" />
        {!editingMap && (
          <>
            <button
              className="btn btn-ghost btn-sm"
              onClick={startEditMap}
              disabled={!profileId || !serverPlan || isDemoProfile}
              title={!profileId ? "Sign in to edit" : (!serverPlan ? "Run analysis first" : "")}
            >
              <window.I.Edit size={13} />
              Edit map
            </button>
            <button
              className="btn btn-ghost btn-sm"
              onClick={rerunAnalysis}
              disabled={!profileId || rerunning || isDemoProfile}
              title={!profileId ? "Sign in to re-run analysis" : ""}
            >
              <window.I.Refresh
                size={13}
                style={rerunning ? { animation: "spin 900ms linear infinite" } : null}
              />
              {rerunning ? "Re-running…" : (serverPlan ? "Re-run analysis" : "Run analysis")}
            </button>
          </>
        )}
      </div>

      {mapError && (
        <div className="field-error-text" style={{ margin: "0 4px 10px" }}>
          {mapError}
        </div>
      )}

      <window.OpportunityMapScreen
        mode="view"
        searchPlan={planInView}
        editable={editingMap}
        saving={savingMap}
        onChange={setEditPlan}
        onSave={saveMap}
        onCancelEdit={cancelEditMap}
      />
    </div>
  );
}

function ProfileBlock({ label, children }) {
  return (
    <div className="profile-block">
      <div style={{
        fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.08em", color: "var(--text-tertiary)", marginBottom: 6,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: "var(--text-primary)", lineHeight: 1.55 }}>
        {children}
      </div>
    </div>
  );
}

function AddSubredditRow({ value, onChange, onSubmit, onCancel, busy }) {
  const ref = React.useRef(null);
  React.useEffect(() => { ref.current?.focus(); }, []);
  return (
    <div className="ob2-subrow ob2-subrow-add">
      <span className="ob2-add-leading">r/</span>
      <input
        ref={ref}
        className="ob2-add-input"
        placeholder="name or reddit.com/r/… URL"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") onSubmit();
          if (e.key === "Escape") onCancel();
        }}
        disabled={busy}
      />
      <button className="btn btn-secondary btn-sm" onClick={onCancel} disabled={busy}>
        Cancel
      </button>
      <button
        className="btn btn-primary btn-sm"
        onClick={onSubmit}
        disabled={busy || !value.trim()}
      >
        {busy ? "Adding…" : "Add"}
      </button>
    </div>
  );
}

window.ProfileScreen = ProfileScreen;
